<template>
  <OnClickOutside @trigger="collapse">
    <div
      :class="{
        'form-selector': true,
        error,
        grayed,
        expanded,
        active,
      }"
    >
      <div
        :class="{
          'selector-box': true,
          placeholder: placeholderValue === modelValue,
          unfocused: !expanded && placeholderValue === modelValue,
        }"
        @click="toggleExpand"
        @keypress.enter="toggleExpand"
      >
        {{ placeholderValue === modelValue ? placeholder : selectedData.name }}

        <div :class="{ 'drop-down-arrow': true, grayed }">
          <inline-svg :src="$assets.svg['arrowIcon']" height="100%" />
        </div>
      </div>

      <transition name="expand" @after-leave="active = false" @after-enter="active = true">
        <ul class="selector-data" v-show="expanded">
          <li class="data-item-placeholder">
            {{ placeholder }}
            <inline-svg
              :src="$assets.svg['arrowIcon']"
              class="drop-down-arrow"
              width="12px"
              height="10px"
              fill="none"
              stroke="#555555"
            />
          </li>

          <li
            v-for="(dataItem, index) in data"
            :key="`data-${index}`"
            class="data-item"
            @click="selectData(dataItem.value)"
            @keypress.enter="selectData(dataItem.value)"
            :class="{
              selected: selectedData && dataItem.value === selectedData.value,
            }"
          >
            <div class="item-cover" :key="`cover-${index}`" />
            <div class="item-text">{{ dataItem.name }}</div>
          </li>
        </ul>
      </transition>
    </div>
  </OnClickOutside>
</template>

<script setup>
/**
 *
 * TODO: incorporate with FormSearchSelector as to not have multiple selector components.
 *
 */
import { OnClickOutside } from '@vueuse/components';
import { ref, computed } from 'vue';

import $assets from '@/assets';

const emit = defineEmits(['update:modelValue']);
const props = defineProps({
  placeholder: {
    type: String,
    default: '',
  },
  placeholderValue: {
    type: Number,
    default: 0,
  },

  /**
   * data: [ {
   *   value: Any,
   *   name: String/Any (cast into string in template)
   * } ]
   *
   * value is returned by the update event, name is the display name in the selection list
   */
  data: {
    type: Array,
    required: true,
  },
  modelValue: {
    default: 0,
  },
  error: {
    type: Boolean,
    default: false,
  },
  grayed: {
    type: Boolean,
    default: false,
  },
});

const expanded = ref(false);
const active = ref(false);

const selectedData = computed(() => {
  return props.data.find((dataItem) => props.modelValue === dataItem.value);
});

function collapse() {
  expanded.value = false;
}

function selectData(key) {
  emit('update:modelValue', key);
  collapse();
}

function toggleExpand() {
  expanded.value = !expanded.value;
}
</script>

<style scoped>
.form-selector {
  position: relative;
  z-index: 1;
  width: 100%;
  height: auto;
  color: #6d6e71;
  font-size: 1.1em;
}
.form-selector.active {
  z-index: 2;
}
.form-selector.expanded:not(.active) {
  z-index: 3;
}

.form-selector .selector-box {
  position: relative;
  box-sizing: border-box;
  white-space: nowrap;
  width: 100%;
  border: 1px solid #6d6e71;
  border-radius: 0.5em;
  cursor: pointer;
  padding: 0.2em 0.4em;
  transition: all 0.3s;
}
.form-selector.grayed .selector-box {
  border-color: #a7a9ac;
  color: #a7a9ac;
}
.form-selector.error .selector-box {
  background: rgb(255, 207, 207);
}

.drop-down-arrow {
  position: absolute;
  right: 0.7em;
  top: calc(50% - 0.35em);
  height: 0.7em;
  display: flex;
  align-items: center;
  justify-content: center;
}
.drop-down-arrow :deep(svg),
.drop-down-arrow :deep(svg) path {
  fill: none;
  stroke: #000;
}
.drop-down-arrow.grayed :deep(svg),
.drop-down-arrow.grayed :deep(svg) path {
  fill: none;
  stroke: #555;
}

/** Selector Option list */
.selector-data {
  position: absolute;
  top: 0;
  left: 0;

  box-sizing: border-box;
  min-width: 100%;
  border: 1px solid #6d6e71;
  border-radius: 0.5em;
  padding: 0 0.4em;
  background: #fff;
  box-shadow: 0 0 10px 0 #a7a9ac;
  overflow: hidden;

  transition: 0.3s all;
  transform-origin: top center;
}

.data-item-placeholder {
  position: relative;
  white-space: nowrap;
  border-bottom: 1px solid #6d6e71;
  padding: 0.4em 0.6em;
  background: #fff;
  color: #acaca6;
}

.data-item {
  position: relative;
  white-space: nowrap;
  padding: 0.4em 0.6em;
  background: #fff;
  cursor: pointer;
  transition: 0.1s all;
}
.data-item:not(:last-child) {
  border-bottom: 1px solid #6d6e71;
}
.data-item .item-text {
  position: relative;
  background: transparent;
}
.data-item:hover .item-text,
.data-item.selected .item-text {
  color: #fff;
}
.data-item:hover .item-cover,
.data-item.selected .item-cover {
  position: absolute;
  left: -0.4em;
  top: -1px;
  width: calc(100% + 0.8em);
  height: calc(100% + 2px);
  background: #83c231;
}

.expand-enter-active,
.expand-leave-active {
  transition: 0.3s all;
  transform-origin: top center;
}
.expand-enter,
.expand-leave-to {
  transform: scaleY(0);
}
.expand-enter-to,
.expand-leave {
  transform: scaleY(1);
}
</style>
